import { formatDate } from '@helpers/date';

/**
 * City-expansion promo codes: one free job post for jobs in the code's cities, once per employer
 * and once per company. The rules that decide anything live on the backend (PromoCodeService);
 * these are the form's own checks, mirroring them so the admin is told here rather than by a
 * refusal after pressing Save. Kept out of the components so they can be tested without rendering.
 */

/** ACTIVE | INACTIVE | EXPIRED | FULL, derived by the backend from active, expiry and the cap. */
export type PromoStatus = 'ACTIVE' | 'INACTIVE' | 'EXPIRED' | 'FULL';

export interface PromoCode {
  _id: string;
  code: string;
  description?: string | null;
  targetCities: string[];
  active: boolean;
  status: PromoStatus;
  expiresAt?: string | null;
  maxRedemptions?: number | null;
  redemptionCount: number;
  jobValidityDays?: number | null;
  createdAt?: string | null;
  createdBy?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

export interface PromoRedemptionRow {
  subscriptionId: string;
  employerId?: string | null;
  employerName?: string | null;
  companyId?: string | null;
  companyName?: string | null;
  redeemedAt?: string | null;
  status?: string | null;
  /** The city of the job the free post paid for; null while it is unspent. */
  postedCity?: string | null;
}

/** What the create form and the settings section edit. Numbers stay strings until they are sent. */
export interface PromoForm {
  code: string;
  description: string;
  targetCities: string[];
  active: boolean;
  /** yyyy-mm-dd, or '' for no expiry. */
  expiresAt: string;
  /** '' for no limit. */
  maxRedemptions: string;
  jobValidityDays: string;
}

export const PROMO_CODE_PATTERN = /^[A-Z0-9-]{4,32}$/;
/** A paid single post lasts 2 days; a free expansion post needs longer to find anyone. */
export const DEFAULT_JOB_VALIDITY_DAYS = '30';

export const normalizeCode = (value: string): string => value.trim().toUpperCase();

/** A new code starts inactive: create it, check the offer page on staging, then switch it on. */
export const emptyPromoForm = (): PromoForm => ({
  code: '',
  description: '',
  targetCities: [],
  active: false,
  expiresAt: '',
  maxRedemptions: '',
  jobValidityDays: DEFAULT_JOB_VALIDITY_DAYS,
});

export const promoFormFrom = (p: PromoCode): PromoForm => ({
  code: p.code,
  description: p.description ?? '',
  targetCities: p.targetCities ?? [],
  active: !!p.active,
  expiresAt: p.expiresAt ? p.expiresAt.slice(0, 10) : '',
  maxRedemptions: p.maxRedemptions != null ? String(p.maxRedemptions) : '',
  jobValidityDays: p.jobValidityDays != null ? String(p.jobValidityDays) : DEFAULT_JOB_VALIDITY_DAYS,
});

const isWholeNumber = (value: string) => /^\d+$/.test(value.trim());

/** Today as yyyy-mm-dd in the admin's own timezone. toISOString() is UTC, which in IST reads as
    yesterday until 05:30 and would refuse a same-day expiry. */
const localToday = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Field errors, keyed by form field. Empty when the form can be sent. `today` is injectable so the
 * past-expiry rule can be tested without a clock.
 *
 * `savedExpiresAt` is the expiry the code already has. A past date is refused only when the admin
 * has just chosen it: an expired code keeps its past date, and checking that on every save made an
 * expired code impossible to edit at all, switching it off included.
 */
export const promoFormErrors = (
  form: PromoForm,
  opts: { requireCode: boolean; today?: string; savedExpiresAt?: string },
): Partial<Record<keyof PromoForm, string>> => {
  const errors: Partial<Record<keyof PromoForm, string>> = {};
  if (opts.requireCode && !PROMO_CODE_PATTERN.test(normalizeCode(form.code))) {
    errors.code = '4 to 32 letters, digits or hyphens, e.g. NEWCITY.';
  }
  if (form.targetCities.length === 0) {
    errors.targetCities = 'Pick at least one city.';
  }
  const max = form.maxRedemptions.trim();
  if (max && (!isWholeNumber(max) || Number(max) < 1)) {
    errors.maxRedemptions = 'A whole number of 1 or more, or leave it empty for no limit.';
  }
  const days = form.jobValidityDays.trim();
  if (!isWholeNumber(days) || Number(days) < 1 || Number(days) > 365) {
    errors.jobValidityDays = 'Between 1 and 365 days.';
  }
  const today = opts.today ?? localToday();
  if (form.expiresAt && form.expiresAt !== opts.savedExpiresAt && form.expiresAt < today) {
    errors.expiresAt = 'Pick today or a later date, or leave it empty for no expiry.';
  }
  return errors;
};

/** CRMStatusChip reads lower-case keys. */
export const statusKey = (status?: string | null): string => (status || 'INACTIVE').toLowerCase();

export const redemptionsLabel = (p: Pick<PromoCode, 'redemptionCount' | 'maxRedemptions'>): string =>
  `${p.redemptionCount ?? 0} / ${p.maxRedemptions != null ? p.maxRedemptions : 'no limit'}`;

export const expiryLabel = (expiresAt?: string | null): string =>
  (expiresAt ? formatDate(expiresAt) : 'No expiry');

export const citiesLabel = (cities: string[], shown = 3): string => {
  if (!cities?.length) return '-';
  const head = cities.slice(0, shown).join(', ');
  return cities.length > shown ? `${head} +${cities.length - shown}` : head;
};
